import React__default, { useRef, useState, useCallback } from 'react';
import { u as useInlinePopover } from '../Popover/useInlinePopover-D_iJ07xC.js';
import { P as PopoverTrigger } from '../Popover/Popover.styles-BPJ5Opwv.js';
import { g as getPlacement } from './utils-BDd8EZN8.js';
import { D as DEFAULT_TRANSITION_DURATION } from './constants-DsghXWMj.js';
import { K as KEYBOARD_KEYNAMES } from '../LocalModules/storm-ui-utils/dist/esm/keyboardKeynames-CVa_vjg3-CVa_vjg3.js';
import { n as noop } from '../LocalModules/storm-ui-utils/dist/esm/noop-BNkHXPFl-BNkHXPFl.js';

function NonInteractiveContainer({
  align,
  children,
  forceOpen,
  inert,
  position,
  spacing,
  transitionDuration,
  trigger,
  fullWidthTrigger,
  triggerAriaDescribedby
}) {
  const timeout = useRef(undefined);
  const [isClosedByUser, setIsClosedByUser] = useState(false);
  const [isFocused, setIsFocused] = useState(false);
  const [isHovered, setIsHovered] = useState(false);
  const isOpen = forceOpen || !inert && !isClosedByUser && (isFocused || isHovered);
  const placement = getPlacement(align, position);
  const {
    attributes,
    styles,
    setPopoverElement,
    setTriggerElement,
    update
  } = useInlinePopover({
    offsetDistance: spacing,
    placement,
    enableFlip: true,
    rootBoundary: 'viewport'
  });
  const handleIsFocused = useCallback(value => {
    setIsFocused(value);
    setIsClosedByUser(false);
  }, []);
  const handleFocus = useCallback(() => {
    handleIsFocused(true);
  }, [handleIsFocused]);
  const handleBlur = useCallback(() => {
    handleIsFocused(false);
  }, [handleIsFocused]);
  const handleIsHovered = useCallback((value, delay = 0) => {
    clearTimeout(timeout.current);
    if (delay) {
      timeout.current = window.setTimeout(() => {
        setIsHovered(value);
      }, delay);
    } else {
      setIsHovered(value);
    }
  }, []);

  /**
   * Handles the mouse hovering over the <Tooltip /> trigger.
   */
  const handleMouseEnterTrigger = useCallback(() => {
    var _transitionDuration$e;
    if (inert) {
      return;
    }
    const duration = typeof transitionDuration === 'number' ? transitionDuration : (_transitionDuration$e = transitionDuration.enter) !== null && _transitionDuration$e !== void 0 ? _transitionDuration$e : DEFAULT_TRANSITION_DURATION.enter;
    handleIsHovered(true, duration);
  }, [handleIsHovered, inert, transitionDuration]);

  /**
   * Handles the mouse leaving the <Tooltip /> trigger.
   */
  const handleMouseLeaveTrigger = useCallback(() => {
    var _transitionDuration$e2;
    const duration = typeof transitionDuration === 'number' ? transitionDuration : (_transitionDuration$e2 = transitionDuration.exit) !== null && _transitionDuration$e2 !== void 0 ? _transitionDuration$e2 : DEFAULT_TRANSITION_DURATION.exit;
    handleIsHovered(false, duration);
  }, [handleIsHovered, transitionDuration]);

  /**
   * Handles keyboard input on the <Tooltip /> trigger.
   *
   * Should close the <Tooltip /> if Esc is clicked.
   */
  const handleKeyDownTrigger = useCallback(event => {
    if (inert) {
      return;
    }
    const {
      key
    } = event;
    if (key === KEYBOARD_KEYNAMES.ESCAPE) {
      setIsClosedByUser(true);
      handleIsHovered(false);
    }
  }, [handleIsHovered, inert]);

  /**
   * Handles the mouse hovering over the <Tooltip /> surface. It should
   * stop the <Tooltip /> from closing.
   *
   * Because it is mouse input, focus should not be visible.
   */
  const handleMouseEnterTooltip = useCallback(() => {
    handleIsHovered(true);
  }, [handleIsHovered]);

  /**
   * Handles the mouse leaving the <Tooltip /> surface. It should
   * close the <Tooltip /> unless it is being forced open.
   *
   * Because it is mouse input, focus should not be visible.
   */
  const handleMouseLeaveTooltip = useCallback(() => {
    handleIsHovered(false);
  }, [handleIsHovered]);
  const handleTooltipEntering = useCallback(isAppearing => {
    if (!isAppearing && update !== null) {
      update();
    }
  }, [update]);
  return /*#__PURE__*/React__default.createElement(React__default.Fragment, null, /*#__PURE__*/React__default.createElement(PopoverTrigger, {
    onKeyDown: handleKeyDownTrigger,
    ref: setTriggerElement,
    onFocus: handleFocus,
    onBlur: handleBlur,
    $fullWidth: fullWidthTrigger
  }, /*#__PURE__*/React__default.cloneElement(trigger, {
    onMouseEnter: handleMouseEnterTrigger,
    onMouseLeave: handleMouseLeaveTrigger,
    'aria-describedby': triggerAriaDescribedby
  })), children({
    attributes,
    handleCloseClicked: noop,
    handleMouseEnter: handleMouseEnterTooltip,
    handleMouseLeave: handleMouseLeaveTooltip,
    handlePopoverEntering: handleTooltipEntering,
    isOpen,
    setPopoverElement,
    styles,
    withCloseButton: false
  }));
}

export { NonInteractiveContainer as N };
//# sourceMappingURL=NonInteractiveContainer-vzTbtk5d.js.map
